import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Linkedin, Target, Search, Clock, ArrowLeft } from "lucide-react";

const linkedInLogoUrl = "https://content.linkedin.com/content/dam/me/business/en-us/amp/brand-site/v2/bg/LI-Logo.svg.original.svg";
export default function LinkedInWizardLanding() {
  const navigate = useNavigate();
  
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-background w-full">
      {/* Header with back button and powered by TailorWiz */}
      <div className="border-b border-border-light bg-card/50 backdrop-blur-sm px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/')}
              className="h-8 w-8 p-0"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <h1 className="text-xl font-semibold text-foreground">LinkedIn Profile Optimization</h1>
            </div>
          </div>
          <div className="text-xs text-muted-foreground font-medium tracking-wide">
            powered by <span className="text-primary font-semibold">TailorWiz</span>
          </div>
        </div>
      </div>
      
      {/* Main content */}
      <div className="bg-gradient-to-br from-background via-muted/20 to-accent/10 p-6">
        <div className="max-w-4xl mx-auto space-y-8">
          
          {/* Hero Section with LinkedIn colors */}
          <div className="text-center space-y-6">
            <div className="flex justify-center">
              <div className="bg-[#0077B5] p-4 rounded-2xl shadow-lg">
                <img 
                  src={linkedInLogoUrl} 
                  alt="LinkedIn" 
                  className="h-12 w-auto filter brightness-0 invert"
                />
              </div>
            </div>
            <div className="space-y-4">
              <h2 className="text-4xl font-bold bg-gradient-to-r from-[#0077B5] via-[#0077B5] to-[#005885] bg-clip-text text-transparent">
                LinkedIn Profile Optimization Wizard
              </h2>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                Transform your LinkedIn profile into a powerful career advancement tool with AI-powered optimization
              </p>
            </div>
          </div>

          {/* Two Option Cards */}
          <div className="grid md:grid-cols-2 gap-6">
            {/* Quick 60-Second Option */}
            <Card className="shadow-xl border-2 border-amber-500/30 hover:border-amber-500/50 transition-all hover:shadow-2xl">
              <CardHeader className="text-center space-y-4 bg-gradient-to-r from-amber-500/10 to-orange-500/10">
                <div className="mx-auto bg-gradient-to-br from-amber-500 to-orange-600 p-4 rounded-full w-fit">
                  <Clock className="h-8 w-8 text-white" />
                </div>
                <CardTitle className="text-2xl bg-gradient-to-r from-amber-600 to-orange-600 bg-clip-text text-transparent">
                  Quick 60-Second Optimization
                </CardTitle>
                <CardDescription className="text-muted-foreground">
                  Perfect when you're short on time. Just upload your resume, specify your target role, and get an optimized profile instantly.
                </CardDescription>
              </CardHeader>
              <CardContent className="text-center space-y-4 pt-4">
                <ul className="text-sm text-muted-foreground space-y-2 text-left">
                  <li className="flex items-center gap-2">
                    <div className="h-1.5 w-1.5 rounded-full bg-amber-500" />
                    Upload resume + target job title
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="h-1.5 w-1.5 rounded-full bg-amber-500" />
                    Add optional notes or documents
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="h-1.5 w-1.5 rounded-full bg-amber-500" />
                    Get optimized profile in seconds
                  </li>
                </ul>
                <Button 
                  onClick={() => navigate('/linkedin-quick-wizard')} 
                  size="lg" 
                  className="bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white shadow-lg px-8 py-4 text-lg font-semibold w-full"
                >
                  <Clock className="h-5 w-5 mr-2" />
                  Quick Start →
                </Button>
              </CardContent>
            </Card>

            {/* Full Optimization Option */}
            <Card className="shadow-xl border-2 border-[#0077B5]/30 hover:border-[#0077B5]/50 transition-all hover:shadow-2xl">
              <CardHeader className="text-center space-y-4 bg-gradient-to-r from-[#0077B5]/10 to-transparent">
                <div className="mx-auto bg-[#0077B5] p-4 rounded-full w-fit">
                  <Linkedin className="h-8 w-8 text-white" />
                </div>
                <CardTitle className="text-2xl text-[#0077B5]">
                  Full Optimization Process
                </CardTitle>
                <CardDescription className="text-muted-foreground">
                  Our comprehensive 12-step wizard that captures every detail for maximum impact. Best for complete profile overhauls.
                </CardDescription>
              </CardHeader>
              <CardContent className="text-center space-y-4 pt-4">
                <ul className="text-sm text-muted-foreground space-y-2 text-left">
                  <li className="flex items-center gap-2">
                    <div className="h-1.5 w-1.5 rounded-full bg-[#0077B5]" />
                    Detailed 12-step guided process
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="h-1.5 w-1.5 rounded-full bg-[#0077B5]" />
                    Custom headlines & value propositions
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="h-1.5 w-1.5 rounded-full bg-[#0077B5]" />
                    Industry-specific optimization
                  </li>
                </ul>
                <Button 
                  onClick={() => navigate('/linkedin-wizard')} 
                  size="lg" 
                  className="bg-[#0077B5] hover:bg-[#005885] text-white shadow-lg px-8 py-4 text-lg font-semibold w-full"
                >
                  <Linkedin className="h-5 w-5 mr-2" />
                  Full Wizard →
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Navigation Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              variant="outline" 
              onClick={() => navigate('/')}
              className="w-full sm:w-auto"
            >
              Back to TailorWiz Dashboard
            </Button>
            <Button 
              variant="secondary" 
              onClick={() => navigate('/linkedin-profiles')}
              className="w-full sm:w-auto"
            >
              Visit LinkedIn Profiles Library
            </Button>
          </div>

          {/* Features Grid */}
          <div className="grid md:grid-cols-3 gap-6">
            <Card className="hover:shadow-lg transition-all duration-300 border-l-4 border-l-[#0077B5]">
              <CardHeader>
                <div className="flex items-center gap-3">
                  <Target className="h-6 w-6 text-[#0077B5]" />
                  <CardTitle className="text-lg">Targeted Optimization</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  Customize your profile for specific industries, roles, and career levels to maximize relevance.
                </p>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-all duration-300 border-l-4 border-l-[#0077B5]">
              <CardHeader>
                <div className="flex items-center gap-3">
                  <Search className="h-6 w-6 text-[#0077B5]" />
                  <CardTitle className="text-lg">Higher Search Rankings</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  Get discovered by recruiters with keyword-optimized content that boosts your profile visibility.
                </p>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-all duration-300 border-l-4 border-l-[#0077B5]">
              <CardHeader>
                <div className="flex items-center gap-3">
                  <Clock className="h-6 w-6 text-[#0077B5]" />
                  <CardTitle className="text-lg">Quick & Easy</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  Complete optimization in minutes with our guided step-by-step wizard process.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}